import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Train } from '../../../trains/models/train';
import { Observable, BehaviorSubject, merge, of, Subscription } from 'rxjs';
import { TrainDataService } from '../../../trains/services/train-data.service';
import { StateService } from '@uirouter/core';
import { TrainScheduleDataService } from '../../services/train-schedule-data.service';
import { TrainSchedule } from '../../models/train-schedule';
import { switchMap } from 'rxjs/operators';
import { ReferenceAreaDataService } from '../../../reference-areas/services/reference-area-data.service';
import { ReferenceArea } from '../../../reference-areas/models/reference-area';
import { TimetableEntry } from '../../models/timetable-entry';
import {MessageService} from "primeng/api";
import {TranslateService} from "@ngx-translate/core";
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-train-schedule-dashboard',
  template: require('./train-schedule-dashboard.component.html'),
  styles: [require('./train-schedule-dashboard.component.sass')]
})
export class TrainScheduleDashboardComponent implements OnInit, OnDestroy {

  projectId: number;

  scheduleDate: Date = new Date(); 
  trains$: Observable<Train[]>;
  trainSchedules$: Observable<TrainSchedule[] | null>;
  timetable$: Observable<TimetableEntry[] | null>;
  referenceAreas$: Observable<ReferenceArea[] | null>;

  dateFormat="dd/mm/yy"
  languageChangeSubscription: Subscription;
  locale = environment.calendar.locales['en'];

  private readonly refreshTrainSchedules$ = new BehaviorSubject(undefined);
  private readonly refreshTimetable$ = new BehaviorSubject(undefined);

  constructor(
    private trainDataService: TrainDataService,
    private trainScheduleDataService: TrainScheduleDataService,
    private referenceAreaDataService: ReferenceAreaDataService,
    public $state: StateService,
    private messageService: MessageService,
    private translate: TranslateService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.locale = environment.calendar.locales[this.translate.currentLang];
    this.projectId = parseInt(this.$state.params && this.$state.params.projectId) ;
    this.trains$ = this.trainDataService.getTrainslist(this.projectId);
    this.trainSchedules$ = this.refreshTrainSchedules$.pipe(
      switchMap(() => merge(
        of(null),
        this.trainScheduleDataService.getTrainSchedule(this.projectId, this.scheduleDate)
        )
      ));
    this.referenceAreas$ = this.referenceAreaDataService.getReferenceAreas(this.projectId);
    this.timetable$ = this.refreshTimetable$.pipe(
      switchMap(() => merge(
        of(null),
        this.trainScheduleDataService.getTimetable(this.projectId)
      ))
    )
   }

  ngOnInit(): void {
    this.refreshTrainSchedules$.next(undefined);
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.locale = environment.calendar.locales[this.translate.currentLang];
    })
  }


  onSelectedDateChange = (selectedDate: Date) => {
    this.scheduleDate = selectedDate;
    this.refreshTrainSchedules$.next(undefined);
    this.refreshTimetable$.next(undefined);
  }

  onTrainIdentifierChanged = () => {

  }

  onSaveTimetableEntry = async (entry: TimetableEntry) => {
    try {
      if (entry.id) {
        await this.trainScheduleDataService.updateTimetableEntry(this.projectId, entry);
      } else {
        await this.trainScheduleDataService.saveTimetableEntry(this.projectId, entry);
      }
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant(entry && entry.id ? "I18N_SCHEDULING.EDIT_TIMETABLE": "I18N_SCHEDULING.CREATE_TIMETABLE"),
        detail:`Timetable ${this.translate.instant(entry && entry.id ? "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_UPDATED" : "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
      this.refreshTrainSchedules$.next(undefined);
    } catch (error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant(entry && entry.id ? "I18N_SCHEDULING.EDIT_TIMETABLE": "I18N_SCHEDULING.CREATE_TIMETABLE"),
        detail: error && error.message
      });
    }
   
  }


  onSaveTrainScheduleEntry = async (trainSchedule: TrainSchedule) => {
    try {
      if (trainSchedule.id) {
        await this.trainScheduleDataService.updateTrainScheduleEntry(this.projectId, trainSchedule);
      } else {
        await this.trainScheduleDataService.saveTrainScheduleEntry(this.projectId, trainSchedule);
      }
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant(trainSchedule && trainSchedule.id ? "I18N_SCHEDULING.EDIT_SCHEDULE": "I18N_SCHEDULING.CREATE_SCHEDULE"),
        detail:`Train schedule ${this.translate.instant(trainSchedule && trainSchedule.id ? "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_UPDATED" : "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
      this.refreshTrainSchedules$.next(undefined);
    } catch (error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant(trainSchedule && trainSchedule.id ? "I18N_SCHEDULING.EDIT_SCHEDULE": "I18N_SCHEDULING.CREATE_SCHEDULE"),
        detail: error && error.message
      });
    }
   
  }

  onDeleteTimetableEntry = async (entry: TimetableEntry) => {
    if (entry && entry.id) {
      const confirmed = await this.confirmationDialogService.confirm(
          `${this.translate.instant('I18N_SCHEDULING.DELETE_TIMETABLE')} ${entry.serviceId}`,
          this.translate.instant('I18N_SCHEDULING.ARE_YOU_SURE_DELETE_TIMETABLE'),
          'danger',
          this.translate.instant('I18N_SCHEDULING.DELETE_TIMETABLE'),
          this.translate.instant('I18N_GENERAL.CANCEL'),
          'lg');
      if(confirmed) {
        try {
          await this.trainScheduleDataService.deleteTimetableEntry(this.projectId, entry);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant('I18N_SCHEDULING.DELETE_TIMETABLE'),
            detail:`Timetable ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")}`
          });
          this.refreshTrainSchedules$.next(undefined);

        }
      catch (error) {
        this.messageService.add({
          severity:'error',
          summary: this.translate.instant('I18N_SCHEDULING.DELETE_TIMETABLE'),
          detail: error && error.message
        });
      }
      }
    }

  }

  onDeleteTrainScheduleEntry = async (trainSchedule: TrainSchedule) => {
    try {
      await this.trainScheduleDataService.deleteTrainScheduleEntry(this.projectId, trainSchedule);
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant('I18N_SCHEDULING.DELETE_SCHEDULE'),
        detail:`Train Schedule ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")}`
      });
      this.refreshTrainSchedules$.next(undefined);
    } catch (error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant('I18N_SCHEDULING.DELETE_SCHEDULE'),
        detail: error && error.message
      });
    }
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }
}
